/**
 * Signing in, through this site's own pages (spec §8.3).
 *
 * The password goes to our API over TLS, which passes it to Cognito; nothing here talks
 * to Cognito's domain, so the user never leaves this origin. The access token is held in
 * memory only. The refresh token is set by the API as a `__Host-` prefixed HttpOnly
 * cookie and is not readable here at all, which is the point.
 *
 * These calls use `fetch` directly rather than `request()` from `./api`: a 401 here means
 * "wrong password", and `request()` would answer it by trying to refresh a session and
 * telling the app the user had been signed out.
 */
import { ApiError, getAccessToken, setAccessToken, type Problem } from './api'
import { loadConfig } from './config'

export type Challenge = 'SOFTWARE_TOKEN_MFA' | 'NEW_PASSWORD_REQUIRED'

export type SignInResult =
  | { state: 'signed-in' }
  | { state: 'challenge'; challenge: Challenge; session: string }

interface SignInBody {
  accessToken?: string
  challenge?: Challenge
  session?: string
}

async function post<T>(path: string, body: unknown): Promise<T | null> {
  const { apiBaseUrl } = await loadConfig()
  const response = await fetch(`${apiBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (response.ok) return response.status === 204 ? null : ((await response.json()) as T)

  let problem: Problem
  try {
    problem = (await response.json()) as Problem
  } catch {
    problem = {
      type: 'about:blank',
      title: 'Sign-in is unavailable',
      status: response.status,
      detail: 'Sign-in is unavailable right now. Try again shortly.',
    }
  }
  throw new ApiError(problem)
}

function settle(body: SignInBody | null): SignInResult {
  if (body?.accessToken) {
    setAccessToken(body.accessToken)
    return { state: 'signed-in' }
  }
  if (body?.challenge && body.session) {
    return { state: 'challenge', challenge: body.challenge, session: body.session }
  }
  throw new Error('unexpected sign-in response')
}

export async function login(email: string, password: string): Promise<SignInResult> {
  return settle(await post<SignInBody>('/auth/login', { email, password }))
}

/** Answer the second step Cognito asked for: an authenticator code, or a new password. */
export async function answerChallenge(
  email: string,
  challenge: Challenge,
  session: string,
  answer: { code?: string; newPassword?: string },
): Promise<SignInResult> {
  return settle(await post<SignInBody>('/auth/challenge', { email, challenge, session, ...answer }))
}

export async function signUp(email: string, password: string): Promise<void> {
  await post('/auth/signup', { email, password })
}

export async function confirmSignUp(email: string, code: string): Promise<void> {
  await post('/auth/confirm', { email, code })
}

export async function resendCode(email: string): Promise<void> {
  await post('/auth/resend', { email })
}

export async function forgotPassword(email: string): Promise<void> {
  await post('/auth/forgot', { email })
}

export async function resetPassword(
  email: string,
  code: string,
  newPassword: string,
): Promise<void> {
  await post('/auth/reset', { email, code, newPassword })
}

/** True when sign-in was refused only because the email address is not confirmed yet. */
export function needsConfirmation(error: unknown): boolean {
  return error instanceof ApiError && error.problem.type.endsWith('/confirmation-required')
}

/** Only ever a path on this site: `returnTo` comes from the URL, so it is untrusted. */
export function safeReturnTo(value: string | null, fallback = '/decks'): string {
  if (!value || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) {
    return fallback
  }
  return value
}

/** The sign-in page, remembering where to go afterwards. `reauth` is for operations that
 *  need a recent password entry (account deletion): the API checks the token's
 *  `auth_time`, and signing in again is what refreshes it. */
export function loginPath(options: { returnTo?: string; reauth?: boolean } = {}): string {
  const parameters = new URLSearchParams()
  if (options.returnTo && options.returnTo !== '/') parameters.set('returnTo', options.returnTo)
  if (options.reauth) parameters.set('reauth', '1')
  const query = parameters.toString()
  return query ? `/login?${query}` : '/login'
}

/** Try to resume a session from the refresh cookie, without asking for a password. */
export async function resume(): Promise<boolean> {
  const config = await loadConfig()
  const response = await fetch(`${config.apiBaseUrl}/auth/refresh`, {
    method: 'POST',
    credentials: 'same-origin',
  })
  if (!response.ok) return false
  const body = (await response.json()) as { accessToken: string }
  setAccessToken(body.accessToken)
  return true
}

/**
 * Log out (spec §8.4).
 *
 * The API revokes every refresh token, voids every access token issued so far and clears
 * the cookie. There is no identity-provider session to end: signing in never created one.
 */
export async function logout(): Promise<void> {
  const config = await loadConfig()
  try {
    await fetch(`${config.apiBaseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    })
  } finally {
    setAccessToken(null)
    sessionStorage.clear()
    window.location.assign('/')
  }
}
